import { Button } from "@/component/Button";
import { ExportStatus } from "@/hooks/useExportStatus";
import { useThreadExportProgress } from "@/hooks/useThreadExportProgress";
import { downloadFile } from "@/util/file";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import LoadingIcon from "../assets/loading-icon.svg";

interface ExportProgressProps {
  threadId: string;
  exportStatus: ExportStatus;
  className?: string;
}

const TOTAL_DURATION_MS = 120000;
const MAX_PROGRESS = 99;
const UPDATE_INTERVAL_MS = 100;

const computeProgress = (exportStartAt: number) => {
  const elapsed = Date.now() - exportStartAt;
  if (elapsed <= 0 || exportStartAt === 0) return 0;
  const ratio = (elapsed / TOTAL_DURATION_MS) * MAX_PROGRESS;
  return Math.min(Math.floor(ratio), MAX_PROGRESS);
};

const ExportProgressComponent = observer(function ExportProgressComponent({
  exportStatus,
  threadId,
  className,
}: ExportProgressProps) {
  const { exportProgress, actions } = useThreadExportProgress(threadId);
  const isExporting = !!exportStatus.renderTask;
  const showSuccess = !!exportStatus.resultUrl && !isExporting;

  const [progress, setProgress] = useState<number>(() => {
    if (showSuccess) return 100;
    if (isExporting && exportStatus.renderTask?.create_time) {
      const timestamp = new Date(exportStatus.renderTask.create_time).getTime();
      return isNaN(timestamp) ? 0 : computeProgress(timestamp);
    }
    return 0;
  });

  const [isDismissed, setIsDismissed] = useState<boolean>(
    exportProgress?.dismissed === true
  );

  useEffect(() => {
    setIsDismissed(exportProgress?.dismissed === true);
  }, [exportProgress?.dismissed]);

  useEffect(() => {
    if (!isExporting || !exportStatus.renderTask?.create_time) {
      return undefined;
    }

    const exportTime = new Date(exportStatus.renderTask.create_time).getTime();
    if (isNaN(exportTime)) {
      return undefined;
    }

    setProgress(computeProgress(exportTime));
    const timer = window.setInterval(() => {
      setProgress(computeProgress(exportTime));
    }, UPDATE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isExporting, exportStatus.renderTask?.create_time]);

  const handleDownload = useCallback(
    (shouldDismiss = true) => {
      if (!exportStatus.resultUrl || !exportStatus.resultFilename) return;
      downloadFile(exportStatus.resultUrl, exportStatus.resultFilename);

      if (shouldDismiss) {
        setIsDismissed(true);
      }

      void actions.markDownloaded(shouldDismiss);
    },
    [actions, exportStatus]
  );

  if (!isExporting && !showSuccess) {
    return null;
  }

  if (showSuccess && isDismissed) {
    return null;
  }

  return (
    <div
      className={classNames(
        "absolute top-0 left-0 right-0 flex justify-center z-50",
        className
      )}
    >
      <div className="rounded-full bg-background-elevated-2 backdrop-blur-sm pl-5 text-text-primary text-xs">
        {showSuccess ? (
          <div className="flex items-center gap-4 py-[6px] pr-2">
            <span className="font-medium">🎉 Your final video is ready!</span>
            <div className="flex items-center gap-2">
              <Button
                type="primary"
                className="h-7! px-[10px] text-xs"
                onClick={() => handleDownload(true)}
              >
                Download
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 font-medium py-3 pr-3">
            <LoadingIcon className="animate-spin w-4 h-4 text-primary shrink-0" />
            <span>Rendering your final video...</span>
            <span>{progress}%</span>
          </div>
        )}
      </div>
    </div>
  );
});

export const ExportProgress = ExportProgressComponent;
